import ErrorBoundary from '@/components/ErrorBoundary'
import ContactSelect from '@/components/Form/Shared/ContactSelect'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, GridHookForm } from 'vnetwork-ui'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
} from '../../constants'
import { initServiceTimes } from '../../constants/function'
import { createCustomerContract } from '../../services'
import { Contract } from '../../types'
import ContractsNewLayout from './layout'
import ProductTemplate from './ProductTemplate'
import reformatContractPayload from './reformat'
import ContractsNewSchema from './schema'
import Total from './Total'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import { useContractTour } from './use-contract-tour'
import SLAConfigSection from './SLAConfigSection'
import NDAConfigSection from './NDAConfigSection'
import CustomConfigSection from './CustomConfigSection'
import ContractDatesSection from './ContractDatesSection'
import { generatePathDatacentral, paths } from '@/utils/path'

const New = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const today = dayjs()
    const [search] = useSearchParams()

    const defaultValues = useMemo(
        () => ({
            customer_id: Number(search.get('customer_id') || 0),
            contract_effective_date: today,
            contract_expiry_date: today.add(1, 'year').subtract(1, 'day'),
            contract_value: 0,
            contract_note: '',
            contract_role: 'customer' as const,
            contract_direction: 'earn' as const,
            customer_contact_id: Number(search.get('contact_id') || 0),
            customer_journey_id: Number(search.get('customer_journey_id') || 0),
            use_contract_id: false,
            contract_id: 0,
            partner_id: null,
            currency_code: 'VND',
            exchange_rate: 1,
            sla_enable: false,
            nda_enable: false,
            nda_attachments: [],
            service_sla: {
                sla_source: 'template' as const,
                uptime_guarantee: 99.9,
                downtime_allowance_minutes: 43,
                business_hours_only: true,
                business_hours_config: {
                    timezone: 'Asia/Ho_Chi_Minh',
                    work_days: [1, 2, 3, 4, 5],
                    work_hours: {
                        start: '08:00',
                        end: '17:30',
                    },
                },
                sla_rules: [
                    {
                        ticket_type_group: 'default',
                        response_time_minutes: 30,
                        resolution_time_minutes: 240,
                    },
                ],
            },
            order: {
                order_source: 'dc',
                order_amount: 0,
                order_note: '',
                order_source_name: 'customer',
                products: [],
            },
        }),
        [search]
    )

    const formControl = useForm<Contract>({
        defaultValues,
        resolver: yupResolver(ContractsNewSchema as any),
    })

    // Auto-save form to localStorage (draft persistence)
    const { clearOnly } = useFormPersistence(
        formControl,
        'contract-form-draft',
        defaultValues
    )

    const customer_id = useWatch({
        control: formControl.control,
        name: 'customer_id',
    })
    const customerContactId = useWatch({
        control: formControl.control,
        name: 'customer_contact_id',
    })
    const contractRole = useWatch({
        control: formControl.control,
        name: 'contract_role',
    })
    const [contactOptions, setContactOptions] = useState<
        { value?: number; contact_roles?: string[] }[]
    >([])

    const selectedContactRoles = useMemo(() => {
        return (
            contactOptions.find((option) => option.value === customerContactId)
                ?.contact_roles || []
        )
    }, [contactOptions, customerContactId])

    const contractRoleOptions = useMemo(() => {
        return CONTRACT_ROLE_OPTIONS(t).filter((option) => {
            return (
                option.value !== 'supplier' ||
                selectedContactRoles.includes('supplier')
            )
        })
    }, [selectedContactRoles, t])

    // Ref để track xem đã auto-add product chưa
    const hasAutoAddedProductRef = useRef(false)

    const { append, fields, remove, insert } = useFieldArray({
        name: 'order.products',
        control: formControl.control,
    })

    // Tour for Contract Create form - must be after formControl and append are defined
    const { startTour } = useContractTour({ formControl, append })
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Quick Help options for Contract creation
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'select-customer',
                label: 'Chọn khách hàng',
                description: 'Chọn KH để tạo hợp đồng',
                selector: '[data-tour="customer-select"]',
                tourTitle: 'Chọn Khách Hàng',
                tourDescription:
                    'Tìm và chọn khách hàng cần tạo hợp đồng. Có thể tìm theo tên, email hoặc mã KH.',
            },
            {
                id: 'add-product',
                label: 'Thêm sản phẩm/dịch vụ',
                description: 'Thêm dịch vụ vào hợp đồng',
                buttonLabel: t('add-services'),
                tourTitle: 'Thêm Sản Phẩm',
                tourDescription:
                    'Nhấn để thêm dịch vụ mới vào hợp đồng. Chọn loại dịch vụ (Cloud, Server, CDN...)',
            },
            {
                id: 'config-product',
                label: 'Cấu hình sản phẩm',
                description: 'Điền thông số kỹ thuật',
                selector: '[data-tour="product-config"]',
                tourTitle: 'Cấu Hình Sản Phẩm',
                tourDescription:
                    'Điền các thông số kỹ thuật: vCPU, RAM, bandwidth, storage tùy theo loại dịch vụ.',
            },
            {
                id: 'set-pricing',
                label: 'Thiết lập giá',
                description: 'Nhập giá và chu kỳ thanh toán',
                selector: '[data-tour="pricing-section"]',
                tourTitle: 'Thiết Lập Giá',
                tourDescription:
                    'Nhập đơn giá, số lượng, chu kỳ thanh toán. Thành tiền tự động tính.',
            },
            {
                id: 'contract-dates',
                label: 'Thời hạn hợp đồng',
                description: 'Ngày hiệu lực và hết hạn',
                selector: '[data-tour="contract-dates"]',
                tourTitle: 'Thời Hạn Hợp Đồng',
                tourDescription:
                    'Chọn ngày hiệu lực và ngày hết hạn hợp đồng. Mặc định 1 năm.',
            },
            {
                id: 'config-sla',
                label: 'Cấu hình SLA',
                description: 'Cam kết chất lượng dịch vụ',
                selector: '[data-tour="sla-config-section"]',
                tourTitle: 'Cấu Hình SLA',
                tourDescription:
                    'SLA (Service Level Agreement) định nghĩa cam kết chất lượng:\n• Uptime: % thời gian dịch vụ hoạt động\n• Response time: Thời gian phản hồi ticket\n• Resolution time: Thời gian xử lý ticket',
            },
            {
                id: 'submit-contract',
                label: 'Tạo hợp đồng',
                description: 'Hoàn tất và lưu hợp đồng',
                buttonLabel: t('create-contract'),
                tourTitle: 'Tạo Hợp Đồng',
                tourDescription:
                    'Nhấn để tạo hợp đồng. Hệ thống sẽ validate và lưu hợp đồng mới.',
            },
        ],
        [t]
    )

    useQuickHelp(quickHelpOptions)

    const products = useWatch({
        control: formControl.control,
        name: 'order.products',
    })

    // Warn (don't block) when any product configoption's commitment_time differs
    // from the period length — affects statement overage bucket cadence even
    // though register fee is unaffected.
    const commitmentMismatches = useMemo(() => {
        return (products ?? [])
            .map((product, index) => {
                const start = product?.service_times?.first_startdate
                const end = product?.service_times?.first_enddate
                if (!start || !end) return null
                const periodMonths = dayjs(end).diff(dayjs(start), 'month')
                if (periodMonths <= 0) return null
                const mismatched = (product.service_configoptions ?? []).find(
                    (c) =>
                        c?.commitment_time != null &&
                        Number(c.commitment_time) !== periodMonths
                )
                if (!mismatched) return null
                return {
                    product_index: index + 1,
                    commitment_time: Number(mismatched.commitment_time),
                    period_months: periodMonths,
                }
            })
            .filter(
                (
                    m
                ): m is {
                    product_index: number
                    commitment_time: number
                    period_months: number
                } => m != null
            )
    }, [products])

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (values: Contract) => {
                return await createCustomerContract(values)
            },
        },
        {
            successMessage: t(`create-contract-success`),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.CUSTOMER_WITH_CONTRACT,
            onSuccess: () => {
                clearOnly() // Clear form draft from localStorage
                navigate(
                    generatePathDatacentral(paths.customerDetail, {
                        id: customer_id,
                    }) + '?tab=contracts'
                )
            },
        }
    )

    const submitCreateContract = async (values: Contract) => {
        await mutateAsync(reformatContractPayload(values))
    }

    const handleAddProduct = () => {
        append({
            product_id: 0,
            product_form: 'commit',
            service_times: initServiceTimes(today),
            service_price_register: 0,
            editable: true,
            prepaid: true,
        })
    }

    const handleDuplicateProduct = (index: number) => {
        const productToDuplicate = products[index]
        if (productToDuplicate) {
            insert(index + 1, {
                ...productToDuplicate,
                editable: true,
            })
        }
    }

    const handleCustomerChange = () => {
        formControl.setValue('customer_contact_id', 0)
        formControl.setValue('contract_role', 'customer')
        formControl.setValue('order.products', [])
        setContactOptions([])
        // Reset ref khi đổi customer để có thể auto-add lại
        hasAutoAddedProductRef.current = false
    }

    // Reset ref khi customer_id thay đổi
    useEffect(() => {
        hasAutoAddedProductRef.current = false
    }, [customer_id])

    useEffect(() => {
        if (
            contractRole &&
            contractRoleOptions.some((option) => option.value === contractRole)
        ) {
            return
        }

        formControl.setValue(
            'contract_role',
            contractRoleOptions[0].value as Contract['contract_role']
        )
    }, [contractRole, contractRoleOptions, formControl])

    const disableSaveButton =
        products?.length === 0 || !products?.[0]?.product_group_name

    useEffect(() => {
        const orderAmount = products?.reduce((total, product) => {
            return total + (product.service_price_register || 0)
        }, 0)

        formControl.setValue(`order.order_amount`, orderAmount || 0)
    }, [products])

    // Tự động mở ProductTemplate khi có customer_id và chưa có products
    useEffect(() => {
        // Chỉ chạy khi đã có customer_id và chưa auto-add
        if (!customer_id || hasAutoAddedProductRef.current) {
            return
        }

        // Kiểm tra xem đã có products chưa
        const currentProducts = formControl.getValues('order.products')
        if (!currentProducts || currentProducts.length === 0) {
            hasAutoAddedProductRef.current = true
            append({
                product_id: 0,
                product_form: 'commit',
                service_times: initServiceTimes(today),
                service_price_register: 0,
                editable: true,
                prepaid: true,
            })
        }
    }, [customer_id, append, formControl, today])

    return (
        <ErrorBoundary>
            <FormProvider {...formControl}>
                <ContractsNewLayout>
                    <SubHeaders
                        actions={[]}
                        breadcrumbs={[
                            {
                                path: '/customers/contracts/new',
                                title: t('contracts-new'),
                            },
                        ]}
                    />

                    <div
                        className="p-4 bg-white rounded-[8px] mt-2"
                        data-tour="contract-form"
                    >
                        <GridHookForm
                            formControl={formControl}
                            actions={[
                                {
                                    title: t('select-service'),
                                    type: 'button',
                                    variant: 'outlined',
                                    startIcon: <AddIcons />,
                                    disabled: !customer_id || isPending,
                                    handler: handleAddProduct,
                                    id: 'add-product-button',
                                },
                                {
                                    title: t('save'),
                                    type: 'submit',
                                    loading: isPending,
                                    variant: 'contained',
                                    disabled: isPending || disableSaveButton,
                                    id: 'save-contract-button',
                                },
                            ]}
                            data={[
                                // === THÔNG TIN KHÁCH HÀNG ===
                                {
                                    renderEditComponent: (control) => {
                                        return (
                                            <div data-tour="contract-customer">
                                                <CustomerSelect
                                                    control={control}
                                                    name="customer_id"
                                                    showLabel
                                                    onChangeAfterHookForm={
                                                        handleCustomerChange
                                                    }
                                                />
                                            </div>
                                        )
                                    },
                                    className: 'col-span-2',
                                },
                                {
                                    renderEditComponent: (control) => {
                                        return (
                                            <div data-tour="contract-contact">
                                                <ContactSelect
                                                    t={t}
                                                    control={control}
                                                    customer_id={customer_id}
                                                    name="customer_contact_id"
                                                    placeholder={t(
                                                        'contact_placeholder'
                                                    )}
                                                    afterFetch={(data) =>
                                                        setContactOptions(
                                                            data as {
                                                                value?: number
                                                                contact_roles?: string[]
                                                            }[]
                                                        )
                                                    }
                                                />
                                            </div>
                                        )
                                    },
                                    className: 'col-span-2',
                                },
                                // === THỜI HẠN & TÙY CHỈNH & SLA & NDA ===
                                {
                                    className: 'col-span-4',
                                    renderEditComponent: () => (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <ContractDatesSection />
                                            <SLAConfigSection
                                                control={formControl.control}
                                                setValue={formControl.setValue}
                                            />
                                            <NDAConfigSection
                                                control={formControl.control}
                                                setValue={formControl.setValue}
                                            />
                                            <CustomConfigSection />
                                        </div>
                                    ),
                                },
                                // === THÔNG BÁO ===
                                {
                                    className: 'col-span-4',
                                    renderEditComponent: () => {
                                        return (
                                            <Alert variant="info" className="">
                                                {t(
                                                    'contract-official-service-notice'
                                                )}
                                            </Alert>
                                        )
                                    },
                                },
                                // === CẢNH BÁO commitment_time lệch chu kỳ ===
                                {
                                    className: 'col-span-4',
                                    renderEditComponent: () =>
                                        commitmentMismatches.length > 0 ? (
                                            <Alert
                                                variant="warning"
                                                className="border rounded-[8px]"
                                            >
                                                <div className="space-y-1">
                                                    {commitmentMismatches.map(
                                                        (m) => (
                                                            <div
                                                                key={
                                                                    m.product_index
                                                                }
                                                                className="text-orange-300 font-medium"
                                                            >
                                                                {t('product')} #
                                                                {
                                                                    m.product_index
                                                                }
                                                                :{' '}
                                                                {t(
                                                                    'commitment-time-mismatch',
                                                                    {
                                                                        commitment_time:
                                                                            m.commitment_time,
                                                                        period_months:
                                                                            m.period_months,
                                                                    }
                                                                )}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </Alert>
                                        ) : (
                                            <></>
                                        ),
                                },
                                // === SẢN PHẨM/DỊCH VỤ ===
                                {
                                    renderEditComponent: () => {
                                        return (
                                            <div
                                                className="space-y-4"
                                                data-tour="contract-products"
                                            >
                                                {fields.map((item, index) => {
                                                    return (
                                                        <div
                                                            key={item?.id}
                                                            className="form-product"
                                                        >
                                                            <ProductTemplate
                                                                index={index}
                                                                remove={remove}
                                                                duplicate={
                                                                    handleDuplicateProduct
                                                                }
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )
                                    },
                                    className:
                                        'xl:col-span-4 lg:col-span-3 md:col-span-2 col-span-2',
                                },
                                // === TỔNG & GHI CHÚ ===
                                {
                                    renderEditComponent: () => {
                                        return (
                                            <div data-tour="contract-total">
                                                <Total />
                                            </div>
                                        )
                                    },
                                    className: 'col-span-4',
                                },
                                {
                                    formItemProps: {
                                        name: 'contract_role',
                                        type: 'select',
                                        placeholder: t(
                                            'contract:contract_role'
                                        ),
                                        label: t('contract:contract_role'),
                                        options: contractRoleOptions,
                                    },
                                    className:
                                        'xl:col-span-2 lg:col-span-2 md:col-span-1 col-span-1',
                                },
                                {
                                    formItemProps: {
                                        name: 'contract_direction',
                                        type: 'select',
                                        placeholder: t(
                                            'contract:contract_direction'
                                        ),
                                        label: t('contract:contract_direction'),
                                        options: CONTRACT_DIRECTION_OPTIONS(t),
                                    },
                                    className:
                                        'xl:col-span-2 lg:col-span-2 md:col-span-1 col-span-1',
                                },
                                {
                                    formItemProps: {
                                        name: 'contract_note',
                                        type: 'textarea',
                                        placeholder: t('note'),
                                        label: t('note'),
                                    },
                                    className:
                                        'xl:col-span-4 lg:col-span-3 md:col-span-2 col-span-2',
                                },
                            ]}
                            onSubmit={(values) => submitCreateContract(values)}
                        />
                    </div>
                </ContractsNewLayout>
            </FormProvider>
        </ErrorBoundary>
    )
}

export default New
